const releaseInstances = new WeakMap();

function initializeFeaturedRelease(section) {
  if (releaseInstances.has(section)) return;

  const mobile = window.matchMedia('(max-width: 1023px)');
  const track = section.querySelector('[data-release-mobile-track]');
  const pagination = section.querySelector('[data-release-mobile-pagination]');
  const slides = [...(track?.children || [])];
  const imageDialog = section.querySelector('[data-release-image-dialog]');
  const fullImage = section.querySelector('[data-release-full-image]');
  const mobileAbort = new AbortController();
  const mobileOptions = { signal: mobileAbort.signal };
  const dots = slides.map((slide, index) => {
    const dot = document.createElement('button');
    dot.type = 'button';
    dot.setAttribute('aria-label', `${index + 1} / ${slides.length}`);
    dot.setAttribute('aria-current', String(index === 0));
    dot.addEventListener('click', () => track.scrollTo({ left: index * track.clientWidth, behavior: 'smooth' }), mobileOptions);
    pagination?.append(dot);
    return dot;
  });
  const galleryArrow = (direction, label) => {
    const button = document.createElement('button');
    button.type = 'button';
    button.className = 'dv-featured-release__gallery-arrow';
    button.textContent = direction < 0 ? '←' : '→';
    button.setAttribute('aria-label', label);
    button.addEventListener('click', () => {
      const index = Math.round(track.scrollLeft / track.clientWidth);
      track.scrollTo({ left: Math.max(0, Math.min(slides.length - 1, index + direction)) * track.clientWidth, behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'instant' : 'smooth' });
    }, mobileOptions);
    return button;
  };
  const previousArrow = galleryArrow(-1, section.querySelector('.splide__arrow--prev .visually-hidden')?.textContent.trim() || 'Previous');
  const nextArrow = galleryArrow(1, section.querySelector('.splide__arrow--next .visually-hidden')?.textContent.trim() || 'Next');
  previousArrow.disabled = true;
  nextArrow.disabled = slides.length < 2;
  pagination?.prepend(previousArrow);
  pagination?.append(nextArrow);
  track?.addEventListener('scroll', () => {
    const index = Math.round(track.scrollLeft / track.clientWidth);
    dots.forEach((dot, i) => dot.setAttribute('aria-current', String(i === index)));
    previousArrow.disabled = index <= 0;
    nextArrow.disabled = index >= slides.length - 1;
  }, { ...mobileOptions, passive: true });
  section.querySelectorAll('[data-release-enlarge]').forEach(button => {
    button.addEventListener('click', () => {
      const thumbnail = button.querySelector('img');
      const cachedSource = thumbnail.currentSrc || thumbnail.src;
      fullImage.src = cachedSource;
      fullImage.alt = thumbnail.alt;
      imageDialog.showModal();
      const targetWidth = Math.min(1600, Math.ceil(window.innerWidth * window.devicePixelRatio));
      if (thumbnail.naturalWidth < targetWidth) {
        const sharperSource = new URL(button.dataset.fullImage, window.location.href);
        sharperSource.searchParams.set('width', String(targetWidth));
        const sharperImage = new Image();
        sharperImage.src = sharperSource.href;
        sharperImage.decode().then(() => {
          if (imageDialog.open && fullImage.getAttribute('src') === cachedSource) fullImage.src = sharperImage.src;
        }).catch(() => {});
      }
    }, mobileOptions);
  });
  section.querySelector('[data-release-image-close]')?.addEventListener('click', () => imageDialog.close(), mobileOptions);
  imageDialog?.addEventListener('click', event => {
    if (event.target !== imageDialog && event.target !== fullImage) return;
    const bounds = fullImage.getBoundingClientRect();
    const scale = Math.min(bounds.width / fullImage.naturalWidth, bounds.height / fullImage.naturalHeight);
    const imageWidth = fullImage.naturalWidth * scale;
    const imageHeight = fullImage.naturalHeight * scale;
    const left = bounds.left + (bounds.width - imageWidth) / 2;
    const top = bounds.top + (bounds.height - imageHeight) / 2;
    const insideImage = event.clientX >= left && event.clientX <= left + imageWidth && event.clientY >= top && event.clientY <= top + imageHeight;
    if (!insideImage) imageDialog.close();
  }, mobileOptions);
  const inlineVideos = [...section.querySelectorAll('.dv-featured-release__inline-video')];
  const inlineObserver = new IntersectionObserver(entries => {
    entries.forEach(entry => {
      const film = entry.target;
      if (entry.isIntersecting && mobile.matches && !film.dataset.userPaused && !window.matchMedia('(prefers-reduced-motion: reduce)').matches) film.play().catch(() => {});
      else film.pause();
    });
  }, { threshold: 0.6 });
  inlineVideos.forEach(film => {
    film.muted = true;
    inlineObserver.observe(film);
    const toggle = film.parentElement.querySelector('[data-release-inline-toggle]');
    const updateToggle = () => {
      toggle.textContent = film.paused ? '▶' : 'Ⅱ';
      toggle.setAttribute('aria-label', film.paused ? toggle.dataset.playLabel : toggle.dataset.pauseLabel);
    };
    film.addEventListener('play', updateToggle, mobileOptions);
    film.addEventListener('pause', updateToggle, mobileOptions);
    updateToggle();
    toggle.addEventListener('click', () => {
      if (film.paused) { delete film.dataset.userPaused; film.play().catch(() => {}); }
      else { film.dataset.userPaused = 'true'; film.pause(); }
    }, mobileOptions);
  });

  const watch = section.querySelector('[data-release-watch]');
  const video = watch?.querySelector('video');
  const playButton = section.querySelector('[data-release-watch-play]');
  const soundButton = section.querySelector('[data-release-watch-sound]');
  const openButton = section.querySelector('[data-release-watch-open]');
  const closeButton = section.querySelector('[data-release-watch-close]');
  const purchase = section.querySelector('.dv-featured-release__purchase');
  const addButton = purchase?.querySelector('#addToCartButton');
  const buyBar = purchase?.querySelector('.dv-featured-release__buy-bar');
  const buyContainer = buyBar?.closest('.add-to-cart-container');
  const barPlaceholder = document.createElement('div');
  barPlaceholder.setAttribute('aria-hidden', 'true');
  buyBar?.before(barPlaceholder);
  const updateStickyBar = () => {
    if (!buyBar || !buyContainer) return;
    const sticky = mobile.matches && buyContainer.getBoundingClientRect().bottom <= 0 && !section.hasAttribute('data-release-watching');
    if (sticky && !buyBar.classList.contains('dv-featured-release__buy-bar--sticky')) barPlaceholder.style.height = `${buyBar.getBoundingClientRect().height}px`;
    buyBar.classList.toggle('dv-featured-release__buy-bar--sticky', sticky);
    if (!sticky) barPlaceholder.style.height = '0px';
  };
  const stickyObserver = new IntersectionObserver(updateStickyBar, { threshold: 0 });
  if (buyContainer) stickyObserver.observe(buyContainer);
  const watchObserver = new MutationObserver(updateStickyBar);
  watchObserver.observe(section, { attributes: true, attributeFilter: ['data-release-watching'] });
  window.addEventListener('resize', updateStickyBar, mobileOptions);
  mobile.addEventListener('change', updateStickyBar, mobileOptions);

  const inertElements = new Map();
  let closeTimer = 0;
  let previousOverflow = '';
  let previousScroll = 0;
  let opener = null;

  const updateSoundLabel = () => {
    if (!video || !soundButton) return;
    soundButton.setAttribute('aria-label', video.muted ? soundButton.dataset.soundOn : soundButton.dataset.soundOff);
    soundButton.setAttribute('aria-pressed', String(!video.muted));
    soundButton.querySelector('[data-release-sound-on-icon]').toggleAttribute('hidden', video.muted);
    soundButton.querySelector('[data-release-sound-off-icon]').toggleAttribute('hidden', !video.muted);
  };

  const updatePlayState = () => {
    if (!video || !playButton) return;
    playButton.setAttribute('aria-label', video.paused ? playButton.dataset.playLabel : playButton.dataset.pauseLabel);
    playButton.querySelector('[data-release-play-icon]').toggleAttribute('hidden', !video.paused);
    playButton.querySelector('[data-release-pause-icon]').toggleAttribute('hidden', video.paused);
  };

  const makeInert = element => {
    if (!element || inertElements.has(element)) return;
    inertElements.set(element, element.inert);
    element.inert = true;
  };

  const disableBackground = () => {
    makeInert(section.querySelector('.dv-featured-release__media'));
    makeInert(section.querySelector('[data-release-mobile-gallery]'));
    inlineVideos.forEach(film => film.pause());
    makeInert(section.querySelector('.dv-featured-release__explore'));
    const wrapper = section.closest('.shopify-section');
    if (wrapper?.parentElement) {
      Array.from(wrapper.parentElement.children).forEach(element => {
        if (element !== wrapper) makeInert(element);
      });
    }
    makeInert(document.querySelector('header'));
    makeInert(document.querySelector('footer'));
  };

  const restoreBackground = () => {
    inertElements.forEach((wasInert, element) => { element.inert = wasInert; });
    inertElements.clear();
  };

  const finishClose = restoreFocus => {
    section.removeAttribute('data-release-watching');
    watch.hidden = true;
    document.body.style.overflow = previousOverflow;
    restoreBackground();
    window.scrollTo(0, previousScroll);
    if (restoreFocus) opener?.focus({ preventScroll: true });
  };

  const closeWatch = (restoreFocus = true) => {
    if (!watch || watch.hidden || closeTimer) return;
    video?.pause();
    watch.removeAttribute('data-visible');
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      finishClose(restoreFocus);
    } else {
      closeTimer = window.setTimeout(() => {
        closeTimer = 0;
        finishClose(restoreFocus);
      }, 220);
    }
  };

  const openWatch = event => {
    if (!watch || !video || !watch.hidden) return;
    opener = event.currentTarget;
    previousOverflow = document.body.style.overflow;
    previousScroll = window.scrollY;
    video.currentTime = 0;
    video.muted = true;
    updateSoundLabel();
    watch.hidden = false;
    section.setAttribute('data-release-watching', '');
    document.body.style.overflow = 'hidden';
    disableBackground();
    requestAnimationFrame(() => watch.setAttribute('data-visible', ''));
    closeButton?.focus({ preventScroll: true });
    video.play().catch(updatePlayState);
  };

  const togglePlay = () => {
    if (!video) return;
    if (video.paused) video.play().catch(updatePlayState);
    else video.pause();
  };

  const toggleSound = () => {
    if (!video) return;
    video.muted = !video.muted;
    updateSoundLabel();
  };

  const handleKeys = event => {
    if (!watch || watch.hidden) return;
    if (event.key === 'Escape') {
      event.preventDefault();
      closeWatch();
      return;
    }
    if (event.key !== 'Tab') return;
    const focusables = [...purchase.querySelectorAll('button:not([disabled]), input:not([disabled]), select:not([disabled]), a[href]'), ...watch.querySelectorAll('button:not([disabled])')]
      .filter(element => element.getClientRects().length && getComputedStyle(element).visibility !== 'hidden');
    if (!focusables.length) return;
    const first = focusables[0];
    const last = focusables[focusables.length - 1];
    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  };

  openButton?.addEventListener('click', openWatch);
  const handleCloseClick = () => closeWatch();
  closeButton?.addEventListener('click', handleCloseClick);
  playButton?.addEventListener('click', togglePlay);
  soundButton?.addEventListener('click', toggleSound);
  addButton?.addEventListener('click', () => {
    if (!watch?.hidden) closeWatch(false);
  });
  video?.addEventListener('play', updatePlayState);
  video?.addEventListener('pause', updatePlayState);
  video?.addEventListener('ended', updatePlayState);
  document.addEventListener('keydown', handleKeys);

  releaseInstances.set(section, () => {
    stickyObserver.disconnect();
    watchObserver.disconnect();
    buyBar?.classList.remove('dv-featured-release__buy-bar--sticky');
    barPlaceholder.remove();
    mobileAbort.abort();
    inlineObserver.disconnect();
    inlineVideos.forEach(film => film.pause());
    imageDialog?.close();
    if (closeTimer) clearTimeout(closeTimer);
    openButton?.removeEventListener('click', openWatch);
    closeButton?.removeEventListener('click', handleCloseClick);
    playButton?.removeEventListener('click', togglePlay);
    soundButton?.removeEventListener('click', toggleSound);
    video?.removeEventListener('play', updatePlayState);
    video?.removeEventListener('pause', updatePlayState);
    video?.removeEventListener('ended', updatePlayState);
    document.removeEventListener('keydown', handleKeys);
    video?.pause();
    if (watch && !watch.hidden) {
      section.removeAttribute('data-release-watching');
      watch.hidden = true;
      document.body.style.overflow = previousOverflow;
      restoreBackground();
    }
    releaseInstances.delete(section);
  });
}

function initializeFeaturedReleases(root = document) {
  if (root.matches?.('.dv-featured-release')) initializeFeaturedRelease(root);
  root.querySelectorAll('.dv-featured-release').forEach(initializeFeaturedRelease);
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => initializeFeaturedReleases(), { once: true });
} else {
  initializeFeaturedReleases();
}

document.addEventListener('shopify:section:load', event => initializeFeaturedReleases(event.target));
document.addEventListener('shopify:section:unload', event => {
  const section = event.target.matches?.('.dv-featured-release')
    ? event.target
    : event.target.querySelector('.dv-featured-release');
  releaseInstances.get(section)?.();
});
